import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import CategoryDepartmentModel from "./add&updateCategoryDepartmentModel";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ThemeButton from "../../components/CustomComponents/Buttons/ThemeButton/ThemeButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";



const DEPARTMENT_SORT_FIELDS = {
  name: "cD_Name",
  sortOrder: "cD_SortOrder",
  id: "cD_ID",
};

const DEPARTMENT_SORT_LABELS = {
  [DEPARTMENT_SORT_FIELDS.name]: "Name",
  [DEPARTMENT_SORT_FIELDS.sortOrder]: "Sort Order",
  [DEPARTMENT_SORT_FIELDS.id]: "ID",
};

const ManageCategoryDepartments = () => {
  const compID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "categoryDepartments";
  const defaultVisibleColumns = {
    name: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = {
    value: DEPARTMENT_SORT_FIELDS.sortOrder,
    label: DEPARTMENT_SORT_LABELS[DEPARTMENT_SORT_FIELDS.sortOrder],
  };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const departmentsFetchSeqRef = useRef(0);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [departmentToEdit, setDepartmentToEdit] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [departmentsInUseIds, setDepartmentsInUseIds] = useState(new Set());
  const lastSelectedIndex = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const hasFiltersApplied = () => search.trim() !== "";

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: DEPARTMENT_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const fetchDepartments = useCallback(async () => {
    const fetchSeq = ++departmentsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        APIs.GET_ALL_CATEGORY_DEPARTMENTS,
        {
          params: {
            compID,
            Search: search?.trim() || "",
            SortBy: sortBy.value,
            SortOrder: sortOrder.value,
          },
        }
      );

      if (fetchSeq !== departmentsFetchSeqRef.current) return;

      const departmentData = response.data.data || [];
      if (Array.isArray(departmentData)) {
        setDepartments(departmentData);
        if (!search.trim()) {
          applyCatalogTotalFromFetch(setCatalogTotalRecords, departmentData.length);
        }
      } else {
        setDepartments([]);
      }
    } catch (error) {
      if (fetchSeq !== departmentsFetchSeqRef.current) return;
      setDepartments([]);
      showErrorToast("Failed to fetch departments.");
    } finally {
      if (fetchSeq === departmentsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [compID, search, sortBy, sortOrder]);

  const totalRecords = departments.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedDepartments = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return departments.slice(start, start + size);
  }, [departments, pageNumber, pageSize]);

  const getDepartmentGlobalIndex = (department) =>
    departments.findIndex((d) => d.cD_ID === department.cD_ID);

  useDebouncedEffect(() => fetchDepartments(), [fetchDepartments]);

  const fetchDepartmentsInUse = useCallback(async () => {
    if (!compID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_DEPARTMENT_RELATIONS, {
        params: { CompID: compID },
      });
      const relations = response.data?.data || response.data || [];
      const inUse = new Set(Array.isArray(relations) ? relations.map((r) => r.cD_ID || r.CD_ID) : []);
      setDepartmentsInUseIds(inUse);
    } catch {
      setDepartmentsInUseIds(new Set());
    }
  }, [compID]);

  useEffect(() => {
    fetchDepartmentsInUse();
  }, [fetchDepartmentsInUse]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }
    
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    // Migrate previous default (cD_ID) so drag-reorder order is visible on load.
    if (
      !preferences.sortBy ||
      preferences.sortBy?.value === DEPARTMENT_SORT_FIELDS.id
    ) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
      if (preferences.sortBy?.value === DEPARTMENT_SORT_FIELDS.id) {
        dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
      }
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(departmentsFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const handleDepartmentSubmit = async (departmentData, isUpdate) => {
    try {
      setLoading(true);
      const url = isUpdate
        ? APIs.PUT_UPDATE_CATEGORY_DEPARTMENT
        : APIs.POST_ADD_CATEGORY_DEPARTMENT;
      const method = isUpdate ? "put" : "post";
      const response = await axiosInstance[method](url, {
        ...departmentData,
        compID,
      });
      if (response.data.status) {
        const updatedDepartment = response.data.data;
        if (isUpdate) {
          setDepartments((prev) =>
            prev.map((dept) =>
              dept.cD_ID === updatedDepartment.cD_ID ? updatedDepartment : dept
            )
          );
        } else {
          setDepartments((prev) => [...prev, updatedDepartment]);
        }
        showSuccessToast(`Department ${isUpdate ? "Updated" : "Added"}.`);
      } else {

        showErrorToast(response.data.message || "Operation failed.");
      }
    } catch (error) {

      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartmentClick = () => {
    setDepartmentToEdit(null);
    setShowAddDepartmentModal(true);
  };

  const handleEditDepartmentClick = (department) => {
    setDepartmentToEdit(department);
    setShowAddDepartmentModal(true);
  };

  const DeleteDepartment = async (department) => {
    if (departmentsInUseIds.has(department.cD_ID)) {
      await swal({
        title: "Cannot delete department",
        text: `"${department.cD_Name}" is linked to one or more categories. Remove it from all categories first, then you can delete the department.`,
        icon: "warning",
        button: "OK",
      });
      return;
    }

    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${department.cD_Name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    setLoading(true);
    try {
      const response = await axiosInstance.delete(
        APIs.DELETE_CATEGORY_DEPARTMENT,
        {
          params: {
            compID,
            ID: department.cD_ID,
          },
        }
      );
      if (response.data.status) {
        setDepartments((prev) =>
          prev.filter((dept) => dept.cD_ID !== department.cD_ID)
        );
        fetchDepartmentsInUse();
        showSuccessToast("Department is Deleted.");
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      showErrorToast("Something went wrong.");

    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (departmentId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (departments[i]?.cD_ID) {
            newSelected.add(departments[i].cD_ID);
          }
        }
      } else {
        if (newSelected.has(departmentId)) {
          newSelected.delete(departmentId);
        } else {
          newSelected.add(departmentId);
        }
        lastSelectedIndex.current = index;
      }
      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(departments.map((item) => item.cD_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No departments selected for deletion.");
      return;
    }

    const deletableIds = Array.from(selectedItems).filter((id) => !departmentsInUseIds.has(id));
    const inUseCount = selectedItems.size - deletableIds.length;
    if (inUseCount > 0 && deletableIds.length === 0) {
      await swal({
        title: "Cannot delete departments",
        text: "All selected department(s) are linked to one or more categories. Remove them from categories first, then you can delete the departments.",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: deletableIds.length < selectedItems.size
          ? `${inUseCount} selected department(s) are used in categories and will be skipped. Delete ${deletableIds.length} department(s)?`
          : `Are you sure you want to delete ${deletableIds.length} selected departments?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        deletableIds.map((departmentId) =>
          axiosInstance.delete(APIs.DELETE_CATEGORY_DEPARTMENT, {
            params: {
              compID,
              ID: departmentId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchDepartments();
      fetchDepartmentsInUse();
      if (inUseCount > 0) {
        showSuccessToast(`${deletableIds.length} department(s) deleted. ${inUseCount} skipped (used in categories).`);
      } else {
        showSuccessToast(`${deletableIds.length} departments deleted successfully.`);
      }
    } catch (err) {
      showErrorToast("Failed to delete selected departments.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No departments selected for editing.");
      return;
    }

    const selectedDepartments = departments.filter((dept) =>
      selectedItems.has(dept.cD_ID)
    );
    setDepartmentToEdit(selectedDepartments);
    setShowAddDepartmentModal(true);
  };

  const isDragReorderEnabled = !search.trim();

  const ensureSortBySortOrder = useCallback(() => {
    if (sortBy?.value !== DEPARTMENT_SORT_FIELDS.sortOrder) {
      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: DEPARTMENT_SORT_FIELDS.sortOrder,
            label: DEPARTMENT_SORT_LABELS[DEPARTMENT_SORT_FIELDS.sortOrder],
          },
        })
      );
    }
    if (sortOrder?.value !== "ASC") {
      dispatch(
        setSortOrder({ pageKey, sortOrder: { value: "ASC", label: "ASC" } })
      );
    }
  }, [dispatch, pageKey, sortBy?.value, sortOrder?.value]);

  const handleDragStart = (e, department) => {
    if (!isDragReorderEnabled) {
      e.preventDefault();
      return;
    }
    const index = departments.findIndex((d) => d.cD_ID === department.cD_ID);
    if (index < 0) {
      e.preventDefault();
      return;
    }
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    if (!isDragReorderEnabled) return;
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    if (!isDragReorderEnabled) return;
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    if (!isDragReorderEnabled) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex == null ||
      overIndex == null ||
      draggedIndex < 0 ||
      overIndex < 0
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const previousDepartments = [...departments];
    let newDepartments = [...departments];
    const [draggedDepartment] = newDepartments.splice(draggedIndex, 1);
    if (!draggedDepartment) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    newDepartments.splice(overIndex, 0, draggedDepartment);
    newDepartments = newDepartments.map((dept, index) => ({
      ...dept,
      cD_SortOrder: index + 1,
    }));

    // Keep optimistic order — do not refetch by ID/name (that undoes the reorder).
    setDepartments(newDepartments);

    try {
      await axiosInstance.post(
        APIs.POST_UPDATE_CATEGORY_DEPARTMENT_SORT_ORDER,
        {
          compID: compID,
          Updates: newDepartments.map((dept) => ({
            cD_ID: dept.cD_ID,
            cD_SortOrder: dept.cD_SortOrder,
          })),
        }
      );
      // Next loads must use sort-order ASC so the saved order is visible.
      ensureSortBySortOrder();
      showSuccessToast("Department order updated successfully.");
    } catch (error) {
      setDepartments(previousDepartments);
      showErrorToast("Failed to update department order.");
      fetchDepartments();
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No category departments match your search "${search.trim()}". Try different keywords or clear filters to see all departments.`;
    }
    return "There are no category departments to show right now. Add a new department or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || departments.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No category departments found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
<div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">
              Manage Departments
            </li>
          </ol>
          <h1 className="page-header mb-0">Manage Departments</h1>
        </div>
        <div className="ms-auto">
          <ThemeButton icon="fa fa-plus" onClick={handleAddDepartmentClick}>
            Add Category Department
          </ThemeButton>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddDepartmentClick} title="Add Category Department" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Category Departments"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
                columnOrder={["name"]}
                columnLabels={{ name: "Name" }}
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedDepartments.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2" />
                          {visibleColumns.name && (
                            <SortableColumnHeader
                              sortField={DEPARTMENT_SORT_FIELDS.name}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Name
                            </SortableColumnHeader>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedDepartments.map((department) => {
                          const globalIndex = getDepartmentGlobalIndex(department);
                          return (
                            <tr
                              key={department.cD_ID}
                              draggable={isDragReorderEnabled}
                              onDragStart={(e) => handleDragStart(e, department)}
                              onDragEnter={(e) => handleDragEnter(e, globalIndex)}
                              onDragLeave={handleDragLeave}
                              onDragOver={handleDragOver}
                              onDrop={handleDrop}
                              onDragEnd={handleDragEnd}
                              style={{
                                cursor: isDragReorderEnabled ? "grab" : "default",
                              }}
                            >
                              <td className="align-middle">
                                <i
                                  className="fas fa-bars text-secondary"
                                  style={{ cursor: "grab" }}
                                />
                              </td>
                              {visibleColumns.name && (
                                <td
                                  className="align-middle"
                                  onClick={() => handleEditDepartmentClick(department)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {department.cD_Name}
                                </td>
                              )}
                              <td className="align-middle text-center">
                                <TableActionsMenu>
                                  <TableActionsItem
                                    icon="fa fa-edit"
                                    onClick={() => handleEditDepartmentClick(department)}
                                  >
                                    Edit
                                  </TableActionsItem>
                                  <TableActionsItem
                                    danger
                                    icon="fa fa-trash"
                                    onClick={() => DeleteDepartment(department)}
                                    title={
                                      departmentsInUseIds.has(department.cD_ID)
                                        ? "Used in categories – cannot delete"
                                        : undefined
                                    }
                                  >
                                    Delete
                                  </TableActionsItem>
                                </TableActionsMenu>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CategoryDepartmentModel
        isModalOpen={showAddDepartmentModal}
        setIsModalOpen={setShowAddDepartmentModal}
        onDepartmentSubmit={handleDepartmentSubmit}
        compID={compID}
        departmentToEdit={departmentToEdit}
      />
    </div>
  );
};

export default ManageCategoryDepartments;